import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { EyeIcon, TrashIcon, UploadCloudIcon, UserIcon } from './Icons';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from './Modal';

export type PhotoFieldProps = {
  /** Section title above the control (omit for compact chrome). */
  title?: string;
  imageUrl: string | null;
  loading?: boolean;
  filename?: string | null;
  hasImage?: boolean;
  editable?: boolean;
  busy?: boolean;
  error?: string | null;
  emptyLabel?: string;
  /** Optional initials shown when there is no photo (avatar variant). */
  initials?: string | null;
  accept?: string;
  /** Round avatar vs rectangular card. */
  variant?: 'avatar' | 'card';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Action buttons beside the image (default) or centered underneath. */
  actionsPlacement?: 'side' | 'below';
  helperText?: string;
  className?: string;
  onPick: (file: File) => void | Promise<void>;
  onRemove?: () => void | Promise<void>;
};

const SIZE = {
  sm: { box: 'h-14 w-14', card: 'h-16 w-24', text: 'text-sm' },
  md: { box: 'h-20 w-20', card: 'h-24 w-36', text: 'text-lg' },
  lg: { box: 'h-28 w-28', card: 'h-32 w-48', text: 'text-2xl' },
  xl: { box: 'h-36 w-36', card: 'h-44 w-36', text: 'text-3xl' },
} as const;

/**
 * Reusable photo upload / view control (crew, company logo, etc.).
 * Presentational — parents supply blob URL and upload/remove handlers.
 */
export const PhotoField: React.FC<PhotoFieldProps> = ({
  title,
  imageUrl,
  loading = false,
  filename,
  hasImage,
  editable = true,
  busy = false,
  error = null,
  emptyLabel = 'No photo',
  initials,
  accept = 'image/png,image/jpeg,image/gif,image/webp,image/bmp,.png,.jpg,.jpeg,.gif,.webp,.bmp',
  variant = 'avatar',
  size = 'md',
  actionsPlacement = 'side',
  helperText,
  className,
  onPick,
  onRemove,
}) => {
  const { colors } = useTheme();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [viewOpen, setViewOpen] = React.useState(false);
  const showImage = Boolean(imageUrl) || Boolean(hasImage);
  const below = actionsPlacement === 'below';

  const stageClass =
    variant === 'avatar'
      ? cn(
          'flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2 bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900',
          colors.border,
          SIZE[size].box,
        )
      : cn(
          'flex shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-slate-100 dark:bg-slate-800',
          colors.border,
          SIZE[size].card,
        );

  const openView = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (imageUrl) setViewOpen(true);
  };

  const handlePick = async (file: File | undefined) => {
    if (!file) return;
    await onPick(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const actions = (
    <div className={cn('flex gap-1', below ? 'justify-center' : 'flex-row items-center')}>
      {editable ? (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => void handlePick(e.target.files?.[0])}
          />
          <Button
            type="button"
            size="icon"
            variant="secondary"
            className="h-8 w-8"
            disabled={busy}
            title={showImage ? 'Replace photo' : 'Upload photo'}
            aria-label={showImage ? 'Replace photo' : 'Upload photo'}
            onClick={() => inputRef.current?.click()}
          >
            <UploadCloudIcon className="h-3.5 w-3.5" />
          </Button>
          {imageUrl ? (
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="h-8 w-8"
              title="View photo"
              aria-label="View photo"
              onClick={openView}
            >
              <EyeIcon className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          {showImage && onRemove ? (
            <Button
              type="button"
              size="icon"
              variant="danger"
              className="h-8 w-8"
              disabled={busy}
              title="Remove photo"
              aria-label="Remove photo"
              onClick={() => void onRemove()}
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </>
      ) : imageUrl ? (
        <Button
          type="button"
          size="icon"
          variant="secondary"
          className="h-8 w-8"
          title="View photo"
          aria-label="View photo"
          onClick={openView}
        >
          <EyeIcon className="h-3.5 w-3.5" />
        </Button>
      ) : null}
    </div>
  );

  return (
    <>
      <div className={cn(below ? 'flex flex-col items-center gap-3' : 'space-y-1.5', className)}>
        {title ? (
          <p className={cn('text-xs font-medium', colors.textMuted)}>{title}</p>
        ) : null}
        <div className={cn(below ? 'flex flex-col items-center gap-3' : 'flex items-center gap-2')}>
          <button
            type="button"
            title={imageUrl ? 'View photo' : undefined}
            disabled={!imageUrl}
            onClick={openView}
            className={cn(
              stageClass,
              'shadow-sm transition',
              imageUrl && 'cursor-pointer hover:ring-2 hover:ring-sky-400/50',
            )}
          >
            {loading ? (
              <span className={cn('text-xs', colors.textMuted)}>…</span>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={filename || 'Photo'}
                className={cn(
                  'h-full w-full',
                  variant === 'avatar' ? 'object-cover' : 'object-contain p-1',
                )}
              />
            ) : initials ? (
              <span
                className={cn(
                  'font-semibold tracking-wide text-slate-500 dark:text-slate-300',
                  SIZE[size].text,
                )}
              >
                {initials}
              </span>
            ) : (
              <span className="flex flex-col items-center gap-1 px-2">
                <UserIcon className={cn('opacity-40', size === 'xl' ? 'h-10 w-10' : 'h-6 w-6')} />
                <span className={cn('text-center text-[10px] leading-tight', colors.textMuted)}>
                  {emptyLabel}
                </span>
              </span>
            )}
          </button>
          {actions}
        </div>
        {helperText ? (
          <p className={cn('text-[10px] text-center', colors.textMuted)}>{helperText}</p>
        ) : null}
        {error ? <p className="text-[10px] text-red-600 text-center">{error}</p> : null}
      </div>

      <Modal isOpen={viewOpen} onClose={() => setViewOpen(false)} size="lg">
        <ModalHeader>
          <ModalTitle>{filename || title || 'Photo'}</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div
            className={cn(
              'flex min-h-[240px] w-full items-center justify-center overflow-hidden rounded-lg border bg-slate-950 p-6',
              colors.border,
            )}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={filename || 'Photo'}
                className="max-h-[70vh] max-w-full object-contain"
              />
            ) : null}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" size="sm" onClick={() => setViewOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PhotoField;
